import { useState, useCallback, useRef } from 'react';

const CHAT_API_URL = import.meta.env.VITE_CHAT_AGENT_URL || 'http://localhost:3002';
const TOKEN_KEY = 'awl_token';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  toolStatus?: string;
  confirmation?: {
    confirmationId: string;
    tool: string;
    message: string;
    params: Record<string, unknown>;
  };
  productCard?: {
    id: string;
    name: string;
    price: number;
    retailer: string;
    price_change?: number;
  };
}

interface UseChatStreamReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  toolStatus: string | null;
  sendMessage: (text: string) => Promise<void>;
  sendConfirmation: (confirmationId: string, approved: boolean) => Promise<void>;
  clearChat: () => void;
}

export function useChatStream(): UseChatStreamReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const conversationId = useRef<string | null>(null);
  const nextId = useRef(0);

  const genId = () => String(nextId.current++);

  const consumeSSE = useCallback(async (response: Response) => {
    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';
    let currentAssistantId: string | null = null;
    let eventType = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ') && eventType) {
          const data = JSON.parse(line.slice(6));

          switch (eventType) {
            case 'conversation':
              conversationId.current = data.conversation_id;
              break;

            case 'token':
              if (!currentAssistantId) {
                currentAssistantId = genId();
                setMessages(prev => [...prev, { id: currentAssistantId!, role: 'assistant', text: data.text }]);
              } else {
                setMessages(prev =>
                  prev.map(m => m.id === currentAssistantId ? { ...m, text: m.text + data.text } : m)
                );
              }
              break;

            case 'tool_status':
              if (data.status === 'running') {
                setToolStatus(data.tool.replace(/_/g, ' '));
              } else {
                setToolStatus(null);
              }
              break;

            case 'confirm':
              setMessages(prev => [...prev, {
                id: genId(),
                role: 'assistant',
                text: data.message,
                confirmation: {
                  confirmationId: data.confirmation_id,
                  tool: data.tool,
                  message: data.message,
                  params: data.params,
                },
              }]);
              break;

            case 'content':
              if (data.type === 'product_card') {
                setMessages(prev => [...prev, {
                  id: genId(),
                  role: 'assistant',
                  text: '',
                  productCard: data.product,
                }]);
              }
              break;

            case 'error':
              setMessages(prev => [...prev, {
                id: genId(),
                role: 'assistant',
                text: data.message,
              }]);
              break;

            case 'done':
              break;
          }
          eventType = '';
        }
      }
    }
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    setMessages(prev => [...prev, { id: genId(), role: 'user', text }]);
    setIsStreaming(true);
    setToolStatus(null);

    try {
      const response = await fetch(`${CHAT_API_URL}/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId.current,
          message: text,
        }),
      });

      if (!response.ok) {
        setMessages(prev => [...prev, {
          id: genId(),
          role: 'assistant',
          text: "I'm having trouble connecting right now. Please try again.",
        }]);
        return;
      }

      await consumeSSE(response);
    } catch {
      setMessages(prev => [...prev, {
        id: genId(),
        role: 'assistant',
        text: "I'm having trouble connecting right now. Please try again.",
      }]);
    } finally {
      setIsStreaming(false);
      setToolStatus(null);
    }
  }, [consumeSSE]);

  const sendConfirmation = useCallback(async (confirmationId: string, approved: boolean) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || !conversationId.current) return;

    setIsStreaming(true);

    try {
      const response = await fetch(`${CHAT_API_URL}/chat/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: conversationId.current,
          confirmation_id: confirmationId,
          approved,
        }),
      });

      if (!response.ok) {
        setMessages(prev => [...prev, {
          id: genId(),
          role: 'assistant',
          text: "Something went wrong confirming that action. Please try again.",
        }]);
        return;
      }

      await consumeSSE(response);
    } catch {
      setMessages(prev => [...prev, {
        id: genId(),
        role: 'assistant',
        text: "Something went wrong confirming that action. Please try again.",
      }]);
    } finally {
      setIsStreaming(false);
      setToolStatus(null);
    }
  }, [consumeSSE]);

  const clearChat = useCallback(() => {
    setMessages([]);
    conversationId.current = null;
    nextId.current = 0;
    setIsStreaming(false);
    setToolStatus(null);
  }, []);

  return { messages, isStreaming, toolStatus, sendMessage, sendConfirmation, clearChat };
}
